import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Card, Col, Container, Row, Spinner, Alert } from "react-bootstrap";
import axiosInstance from "../../utils/axiosUtil";
import EditReportForm from "./EditReportForm";

export default function EditReportPage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchReport = async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await axiosInstance.get(`/reports/slug/${slug}`);
        setReport(data);
      } catch (err) {
        setError(
          err?.response?.data?.message ||
            `Failed to load report. It may not exist.`
        );
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [slug]);

  const handleChange = (updated) => {
    setReport(updated);
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    if (!report) return;
    setSaving(true);
    setSaveError("");
    setSaveSuccess(false);
    setErrors({});
    try {
      const { data } = await axiosInstance.patch(`/reports/${report._id}`, report);
      setReport(data);
      setSaveSuccess(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setSaveError(err?.response?.data?.message || "Failed to save report.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container fluid className="page-section py-4">
      <Row className="mb-4 align-items-center">
        <Col>
          <h3 className="page-title mb-1">Edit Report</h3>
          {report && <p className="page-subtitle mb-0 text-muted">{report.title}</p>}
        </Col>
        <Col xs="auto" className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={() => navigate("/admin/reports/archive")} disabled={saving}>
            Back to Archive
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving || loading || !report}>
            {saving ? <><Spinner animation="border" size="sm" className="me-2" />Saving...</> : "Save Changes"}
          </Button>
        </Col>
      </Row>

      {saveSuccess && <Alert variant="success" dismissible onClose={() => setSaveSuccess(false)}>Report saved successfully!</Alert>}
      {saveError && <Alert variant="danger" dismissible onClose={() => setSaveError("")}>{saveError}</Alert>}

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" /><p className="mt-3 text-muted">Loading report...</p></div>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : report ? (
        <Card>
          <Card.Body>
            <EditReportForm report={report} onChange={handleChange} errors={errors} />
          </Card.Body>
          <Card.Footer className="d-flex justify-content-end gap-2 py-3">
            <Button variant="outline-secondary" onClick={() => navigate("/admin/reports/archive")} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? <><Spinner animation="border" size="sm" className="me-2" />Saving...</> : "Save Changes"}
            </Button>
          </Card.Footer>
        </Card>
      ) : null}
    </Container>
  );
}
